/**
 * Push Notification Templates Tab
 *
 * Shows a template's own identifiers (Template ID / App ID) alongside its content. Seeded template
 * ranges are per-app (e.g. 57000+ = ACCounselling), so the IDs are how you tell which application a
 * template belongs to and how you match one back to the TemplateID a push actually rendered.
 */

import { useState, useMemo, useEffect } from 'react';
import {
  IconTemplate,
  IconEye,
  IconEdit,
  IconTrash,
  IconPlus,
  IconToggleLeft,
  IconToggleRight,
  IconRefresh,
} from '@tabler/icons-react';
import { Stack, Group, Text, Button, ActionIcon, Badge, Tooltip } from '../../../../../components/ui';
import { DataTable } from '../../../../../components/advanced/data';
import type { DataTableColumn } from '../../../../../components/advanced/data/DataTable';
import { EmptyState } from '../../../../../components/ui/data-display';
import { alert } from '../../../../../components/ui/providers/AlertProvider';
import { useApplicationCatalogueService, type AppManifest } from '@actschurch/ccms-sdk';
import type { PushTemplatesTabProps, PushTemplateRow } from '../types';
import { mapTemplateToRow, decodePushBody, parsePushSpec } from '../types';
import { formatDate } from '../../types';

export function PushTemplatesTab({
  templates,
  loading,
  onRefresh,
  onView,
  onEdit,
  onDelete,
  onCreate,
}: PushTemplatesTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Resolve mobileAppId → a friendly name via the Application Catalogue (the registry of what ships).
  const catalogueService = useApplicationCatalogueService();
  const [catalogue, setCatalogue] = useState<AppManifest[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        let apps: AppManifest[] = [];
        try {
          apps = await catalogueService.getAllApps();
        } catch {
          apps = await catalogueService.getCatalogue();
        }
        if (active) setCatalogue(apps || []);
      } catch {
        // Non-fatal: fall back to showing the raw appId.
        if (active) setCatalogue([]);
      }
    })();
    return () => { active = false; };
  }, [catalogueService]);

  const appName = (appId: string) => {
    const app = catalogue.find((a) => a.appId?.toLowerCase() === appId.toLowerCase());
    return app?.displayName || app?.shortName || appId;
  };

  // Newest first — a freshly created or seeded template belongs at the top, not buried.
  const rows: PushTemplateRow[] = useMemo(
    () => templates.map(mapTemplateToRow).sort((a, b) => (b.templateID || 0) - (a.templateID || 0)),
    [templates]
  );

  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;
    const query = searchQuery.toLowerCase();
    return rows.filter((row) =>
      row.templateName?.toLowerCase().includes(query) ||
      decodePushBody(row.body).toLowerCase().includes(query) ||
      String(row.templateID).includes(query) ||
      String(row.appID ?? '').includes(query) ||
      (row.mobileAppId || '').toLowerCase().includes(query)
    );
  }, [rows, searchQuery]);

  // Don't strand the user on a page past the end after a delete or a narrower search.
  useEffect(() => {
    if (pageSize <= 0) return;
    const lastPage = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    if (page > lastPage) setPage(lastPage);
  }, [filteredRows.length, page, pageSize]);

  const columns: DataTableColumn<PushTemplateRow>[] = useMemo(
    () => [
      {
        key: 'templateID',
        label: 'Template ID',
        width: 110,
        sortable: true,
        render: (value) => (
          <Text size="sm" fw={600} style={{ fontFamily: 'monospace' }}>#{value}</Text>
        ),
      },
      {
        key: 'appID',
        label: 'App ID',
        width: 100,
        sortable: true,
        filterable: true,
        render: (value) => (
          <Tooltip label="Owning application (AppRegistry id)">
            <Badge variant="light" color="grape">{value ?? '—'}</Badge>
          </Tooltip>
        ),
      },
      {
        key: 'templateName',
        label: 'Template Name',
        sortable: true,
        render: (value) => (
          <Group gap="xs" wrap="nowrap">
            <IconTemplate size={16} />
            <Text size="sm" fw={500} lineClamp={1}>{value || 'Unnamed'}</Text>
          </Group>
        ),
      },
      {
        key: 'mobileAppId',
        label: 'Mobile App',
        width: 160,
        sortable: true,
        filterable: true,
        render: (value) => {
          // The string appId ("ccms.task") — the MOBILE app, which is what selects the APNs topic.
          // Distinct from the int App ID column above, which names the owning backend service.
          const appId = value as string | null | undefined;
          if (!appId) {
            return (
              <Tooltip label="Not linked to a mobile app — a push using this template must choose the app itself">
                <Text size="xs" c="dimmed">—</Text>
              </Tooltip>
            );
          }
          return (
            <Tooltip label={appId}>
              <Badge variant="light" color="teal">{appName(appId)}</Badge>
            </Tooltip>
          );
        },
      },
      {
        key: 'body',
        label: 'Message Preview',
        sortable: true,
        render: (value) => {
          // Show what the notification actually SAYS, not the raw spec JSON.
          const decoded = decodePushBody(value as string);
          const spec = parsePushSpec(decoded);
          return (
            <Text size="sm" lineClamp={2} style={{ maxWidth: 360 }}>
              {spec?.body || (spec ? '(No body in spec)' : decoded) || '(No Message)'}
            </Text>
          );
        },
      },
      {
        key: 'isActive',
        label: 'Status',
        width: 130,
        sortable: true,
        filterable: true,
        render: (value) => (
          <Group gap="xs" wrap="nowrap">
            {value ? (
              <>
                <IconToggleRight size={16} color="green" />
                <Badge color="green" variant="light">Active</Badge>
              </>
            ) : (
              <>
                <IconToggleLeft size={16} color="gray" />
                <Badge color="gray" variant="light">Inactive</Badge>
              </>
            )}
          </Group>
        ),
      },
      {
        key: 'createdDateTime',
        label: 'Created',
        width: 160,
        sortable: true,
        render: (value) => <Text size="sm" c="dimmed">{formatDate(value)}</Text>,
      },
      {
        key: 'actions',
        label: 'Actions',
        width: 120,
        render: (_, row) => (
          <Group gap="xs" wrap="nowrap">
            <ActionIcon variant="subtle" color="blue" onClick={(e) => { e.stopPropagation(); onView(row); }} title="View"><IconEye size={16} /></ActionIcon>
            <ActionIcon variant="subtle" color="gray" onClick={(e) => { e.stopPropagation(); onEdit(row); }} title="Edit"><IconEdit size={16} /></ActionIcon>
            <ActionIcon
              variant="subtle"
              color="red"
              title="Delete"
              onClick={async (e) => {
                e.stopPropagation();
                const confirmed = await alert.confirm(
                  'Delete Template',
                  `Delete "${row.templateName}" (#${row.templateID})? Any push that references it falls back to its own title/body.`,
                  { confirmText: 'Delete' }
                );
                if (confirmed) await onDelete(row.templateID);
              }}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ),
      },
    ],
    [onView, onEdit, onDelete, catalogue]
  );

  if (!loading && templates.length === 0) {
    return (
      <Stack gap="md">
        <Group justify="flex-end">
          <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => onRefresh()} loading={loading}>
            Refresh
          </Button>
        </Group>
        <EmptyState
          title="No Push Templates"
          description="Create your first template."
          icon={<IconTemplate size={48} />}
          actionLabel="Create Template"
          onAction={onCreate}
        />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">{filteredRows.length} template(s)</Text>
        <Group gap="xs">
          <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => onRefresh()} loading={loading}>
            Refresh
          </Button>
          <Button leftSection={<IconPlus size={16} />} gradientVariant="info" onClick={onCreate}>
            Create Template
          </Button>
        </Group>
      </Group>
      <DataTable
        data={filteredRows}
        columns={columns}
        loading={loading}
        searchPlaceholder="Search by name, message, template ID or app ID…"
        searchValue={searchQuery}
        onSearch={(query) => { setSearchQuery(query); setPage(1); }}
        onRowClick={(row) => onView(row)}
        pagination={{
          page,
          pageSize,
          total: filteredRows.length,
          onPageChange: setPage,
          onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
        }}
        gradientVariant="primary"
        highlightOnHover
        striped
        withRowAnimations
        enableInternalSort
        enableInternalSearch
      />
    </Stack>
  );
}
